import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  IconButton,
  TextField,
  Button,
  Grow,
  Paper,
  Alert,
  Fade,
  LinearProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  styled,
  createTheme,
  alpha,
  Grid,
  Chip,
  Modal,
  Checkbox,
  FormControlLabel,
  Snackbar,
  AlertTitle,
  Tooltip,
  CircularProgress,
  Collapse,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomPagination from "views/client/pagination";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  Folder as FolderIcon,
  Article as ArticleIcon,
  DeleteOutline as DeleteOutlineIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  AutoMode as AutoModeIcon,
} from "@mui/icons-material";
import addIcon from "../../../assets/img/add_icon.svg";
import download_icon from "../../../assets/img/download_icon.svg";
import colors from "colors/color";
import edit_icon from "../../../assets/img/edit_icon.svg";
// import cancel_icon from "../../../assets/img/cancel_icon.svg";
import cancel_icon from "../../../assets/img/cancel_icon.svg";
import ai_icon from "../../../assets/img/ai_icon2.svg";
import microsofticon from "../../../assets/img/Microsoft.png";
import googleticon from "../../../assets/img/google.png";
import GmailAuth from "../../ScrapedPosts/GmailAuth";
import { NewPostButton } from "components/Buttons/Buttons";
import { useSnackbar } from "hooks/useSbackbar";
import { useNavigate } from "react-router-dom";
import { tooltipClasses } from "@mui/material/Tooltip";
import { useTour } from "context/TourContext";
import useFetchUser from "hooks/useFetchUser";
import useFocusMode from "hooks/useFocusMode";

const theme = createTheme({
  palette: {
    primary: {
      main: "#0017E7",
    },
    secondary: {
      main: "#D9D9D9",
    },
    background: {
      default: "#FFFFFF",
      paper: "#FFFFFF",
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: alpha("#0017E7", 0.05),
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: alpha("#0017E7", 0.04),
          },
        },
      },
    },
  },
});

const CancelButton = styled(Button)(({ theme }) => ({
  width: 115,
  height: 48,
  borderRadius: "10px",
  borderWidth: "1px",
  gap: "10px",
  paddingTop: "14px",
  paddingRight: "24px",
  paddingBottom: "14px",
  paddingLeft: "24px",

  border: "1px solid #FF0000",
  color: "#FF0000",
  "&:hover": {
    backgroundColor: "#FF0000",
    color: "#fff",
  },
}));

const UpdateButton = styled(Button)(({ theme }) => ({
  backgroundColor: colors.primary,
  color: "#fff",
  "&:hover": {
    backgroundColor: "#303f9f",
    color: "#fff",
  },
}));

const StyledTypography = styled(Typography)(() => ({
  color: "#000",
  fontFamily: "Poppins",
  fontWeight: 500,
  fontSize: "14px",
}));

const StyledTextField = styled(TextField)(({ theme, isediting }) => ({
  "& .MuiOutlinedInput-root": {
    height: 56,
    borderRadius: "10px",
    fontFamily: "Poppins",
    borderColor: "#00000033",
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#e0e0e0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
      borderWidth: "2px",
      boxShadow: "0px 0px 2px 0.4pxrgb(0, 231, 135)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
      borderWidth: "2px",
      boxShadow: "0px 0px 3px 0.4px #0017E7",
    },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  height: 56,
  fontFamily: "Poppins",
  borderRadius: "10px",
  backgroundColor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e0e0e0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
    borderWidth: "2px",
    boxShadow: "0px 0px 2px 0.4pxrgb(231, 0, 142)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
    borderWidth: "1px",
    boxShadow: "0px 0px 3px 0.4px #0017E7",
  },
  "& .MuiSelect-select": {
    padding: "16.5px 14px",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  boxShadow: "0 4px 20px rgba(0, 23, 231, 0.05)",
  borderRadius: 12,
  margin: theme.spacing(2, 0),
  overflow: "hidden",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
  padding: theme.spacing(2),
}));

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
  padding: theme.spacing(2),
  color: theme.palette.primary.main,
  fontWeight: 600,
}));

const FileTypeChip = styled(Chip)(({ theme, filetype }) => {
  const getColor = () => {
    switch (filetype?.toLowerCase()) {
      case "pdf":
        return "#FF5252";
      case "doc":
      case "docx":
        return "#2196F3";
      case "jpg":
      case "png":
      case "jpeg":
        return "#4CAF50";
      case "xls":
      case "xlsx":
        return "#8BC34A";
      default:
        return theme.palette.secondary.main;
    }
  };

  return {
    backgroundColor: alpha(getColor(), 0.1),
    color: getColor(),
    fontWeight: 500,
    borderRadius: 8,
  };
});

const BigTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: "0.95rem",
    padding: theme.spacing(1.5),
    maxWidth: 280,
    backgroundColor: "#333",
    color: "#fff",
    borderRadius: "8px",
    boxShadow: theme.shadows[3],
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#333",
  },
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 450,
  backgroundColor: '#fff',
  borderRadius: 20,
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  padding: 0,
  outline: 'none',
}));

const AccountButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3),
  border: '2px solid #e0e0e0',
  borderRadius: 12,
  backgroundColor: '#fff',
  minHeight: 120,
  width: '100%',
  maxWidth: 150,
  textTransform: 'none',
  fontFamily: 'Poppins',
  '&:hover': {
    borderColor: '#0017E7',
    backgroundColor: 'rgba(0, 23, 231, 0.04)',
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
  },
  transition: 'all 0.3s ease',
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 12,
  padding: theme.spacing("12px", "20px"),
  minWidth: 200,
  textTransform: "none",
  fontWeight: 500,
  fontFamily: "Poppins",
  gap: 8,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  border: variant === "outlined" ? "2px solid #0017E7" : "none",
  color: variant === "outlined" ? "#0017E7" : "#fff",
  backgroundColor: variant === "outlined" ? "transparent" : "linear-gradient(135deg, #0017E7 0%, #3D5AFE 100%)",
  boxShadow: variant === "outlined" ? "none" : "0 4px 12px rgba(0, 23, 231, 0.3)",
  "&:hover": {
    backgroundColor: variant === "outlined" ? "rgba(0, 23, 231, 0.08)" : "linear-gradient(135deg, #001AFF 0%, #4A5AFE 100%)",
    borderColor: variant === "outlined" ? "#3D5AFE" : "none",
    transform: "translateY(-2px)",
    boxShadow: variant === "outlined" ? "0 2px 8px rgba(0, 23, 231, 0.2)" : "0 6px 20px rgba(0, 23, 231, 0.4)",
  },
  "&:active": {
    transform: "translateY(0px)",
  },
  "&.Mui-disabled": {
    backgroundColor: variant === "outlined" ? "transparent" : "#E0E0E0",
    border: variant === "outlined" ? "2px solid #E0E0E0" : "none",
    color: "#9E9E9E",
    boxShadow: "none",
    transform: "none",
  },
}));

const ToggleButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1, 2.5), // Reduced padding from (1.5, 3) to (1, 2.5)
  textTransform: "none",
  fontWeight: 600,
  fontFamily: "Poppins",
  fontSize: "13px", // Reduced from 14px
  background: "linear-gradient(135deg, #0017E7 0%, #3D5AFE 100%)",
  color: "#fff",
  minWidth: 180, // Reduced from 200
  height: 36, // Added explicit height to make it smaller
  boxShadow: "0 4px 12px rgba(0, 23, 231, 0.3)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
    transition: "left 0.5s",
  },
  "&:hover": {
    background: "linear-gradient(135deg, #001AFF 0%, #4A5AFE 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(0, 23, 231, 0.4)",
    "&::before": {
      left: "100%",
    },
  },
  "&:active": {
    transform: "translateY(0px)",
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: 160, // Reduced from 180
    fontSize: "12px", // Reduced from 13px
    padding: theme.spacing(0.8, 2), // Reduced from (1.2, 2.5)
    height: 32, // Smaller height for mobile
  },
}));

const EnhancedContainer = styled(Box)(({ theme, isExpanded }) => ({
  // background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", // Removed background
  borderRadius: 20,
  border: "1px solid #e2e8f0",
  boxShadow: isExpanded
    ? "0 20px 60px rgba(0, 0, 0, 0.08), 0 8px 25px rgba(0, 0, 0, 0.06)"
    : "0 4px 20px rgba(0, 0, 0, 0.05)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(135deg, #0017E7 0%, #3D5AFE 100%)",
    backgroundSize: "200% 100%",
    animation: isExpanded ? "gradient 3s ease infinite" : "none",
  },
  "@keyframes gradient": {
    "0%": {
      backgroundPosition: "0% 50%",
    },
    "50%": {
      backgroundPosition: "100% 50%",
    },
    "100%": {
      backgroundPosition: "0% 50%",
    },
  },
}));

const SectionCard = styled(Box)(({ theme }) => ({
  background: "#ffffff",
  borderRadius: 16,
  padding: theme.spacing(2), // Reduced from 2.5 to 2
  border: "1px solid #e2e8f0",
  height: "100%",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "3px",
    // background: "linear-gradient(90deg, #667eea, #764ba2)",
    background: "linear-gradient(135deg, #0017E7 0%, #3D5AFE 100%)",

    transform: "scaleX(0)",
    transformOrigin: "left",
    transition: "transform 0.3s ease",
  },
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.1)",
    "&::before": {
      transform: "scaleX(1)",
    },
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Poppins",
  fontWeight: 700,
  fontSize: "16px",
  color: "#1a202c",
  marginBottom: theme.spacing(2),
  textAlign: "center",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-8px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "40px",
    height: "2px",
    background: "linear-gradient(135deg, #0017E7 0%, #3D5AFE 100%)",
    borderRadius: "1px",
  },
}));

// Move FileIcon component outside of DataImportSection
const FileIcon = ({ fileType }) => {
  switch (fileType?.toLowerCase()) {
    case "pdf":
      return <PdfIcon sx={{ color: "#FF5252" }} />;
    case "doc":
    case "docx":
      return <DescriptionIcon sx={{ color: "#2196F3" }} />;
    case "jpg":
    case "png":
    case "jpeg":
      return <ImageIcon sx={{ color: "#4CAF50" }} />;
    case "folder":
      return <FolderIcon sx={{ color: "#FFC107" }} />;
    default:
      return <ArticleIcon sx={{ color: "#9E9E9E" }} />;
  }
};

// Move ModernFileTable outside of DataImportSection as a separate component
const ModernFileTable = ({ files = [], onDelete, t }) => {
  // Remove internal pagination state - files are already paginated by parent component

  const removeFile = (id) => {
    if (onDelete) onDelete(id);
  };

  // Format file size
  const formatFileSize = (sizeInBytes) => {
    if (sizeInBytes < 1024) return `${sizeInBytes} B`;
    else if (sizeInBytes < 1024 * 1024)
      return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    else return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <StyledTableContainer component={Paper} >
      <Table sx={{ minWidth: 550 }}>
        <TableHead>
          <TableRow>
            <StyledHeaderCell>
              {t ? t("fileName") : "File Name"}
            </StyledHeaderCell>
            <StyledHeaderCell>{t ? t("type") : "Type"}</StyledHeaderCell>
            <StyledHeaderCell>{t ? t("size") : "Size"}</StyledHeaderCell>
            <StyledHeaderCell>
              {t ? t("uploadDate") : "Upload Date"}
            </StyledHeaderCell>
            <StyledHeaderCell align="right">
              {t ? t("action") : "Action"}
            </StyledHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map((file) => (
            <TableRow key={file.id}>
              <StyledTableCell>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ mr: 1.5 }}>
                    <FileIcon fileType={file.type} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {file.name}
                  </Typography>
                </Box>
              </StyledTableCell>
              <StyledTableCell>
                <FileTypeChip
                  label={file.type}
                  filetype={file.type}
                  size="small"
                />
              </StyledTableCell>
              <StyledTableCell>
                <Typography variant="body2">
                  {typeof file.size === "number"
                    ? formatFileSize(file.size)
                    : file.size}
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                <Typography variant="body2" color="text.secondary">
                  {file.uploadDate}
                </Typography>
              </StyledTableCell>
              <StyledTableCell align="right">
                <IconButton
                  sx={{
                    color: "#FF5252",
                    "&:hover": {
                      backgroundColor: alpha("#FF5252", 0.1),
                    },
                  }}
                  onClick={() => removeFile(file.id)}
                  size="small"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </StyledTableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export function DataImportSection({
  AnimatedDataIcon,
  PaginationControls,
  UploadBox,
  VisuallyHiddenInput,
  itemsPerPage,
  categories = [],
  selectedCategory = "",
  newCategory = "",
  isDropdownOpen = false,
  // File management
  files = [],
  dragging = false,
  error = "",
  isUploading = false,
  uploadProgress = 0,
  showSuccessAnimation = false,
  showDataScrapingAnimation = false,
  // Dynamic progress props
  showImportProgress = false,
  importFileCount = 0,
  estimatedTimeRemaining = 0,
  // Functions
  handleCategoryChange = () => { },
  setIsDropdownOpen = () => { },
  deleteCategory = () => { },
  setNewCategory = () => { },
  handleAddCategory = () => { },
  handleDragOver = () => { },
  handleDragLeave = () => { },
  handleFileDrop = () => { },
  handleFileUpload = () => { },
  setError = () => { },
  removeAllFiles = () => { },
  uploadFiles = () => { },
  removeFile = () => { },
  getPaginatedFiles = () => [],

  // Pagination
  page = 1,
  totalPages = 1,
  handlePageChange = () => { },

  onCategorySelect = () => { },
  onSubCategorySelect = () => { },
}) {
  // Add a unique identifier for this component instance for debugging
  const componentId = React.useRef(Math.random().toString(36).substr(2, 9)).current;
  
  console.log(`DataImportSection mounted - Component ID: ${componentId}`);
  
  const { t } = useTranslation();
  const [categoriesNew, setCategoriesNew] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategoryNew, setSelectedCategoryNew] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  // Email scraping states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState('account');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [subject, setSubject] = useState('');
  const [isBlindSearch, setIsBlindSearch] = useState(false);
  const [result, setResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessingOutlook, setIsProcessingOutlook] = useState(false);
  const [showGmailAuth, setShowGmailAuth] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  const [isScrappingLaunched, setIsScrappingLaunched] = useState(false);
  const [state, statechange] = useState(false);
  const [finishing, setfinishing] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showStopMessage, setShowStopMessage] = useState(false);
  const intervalIdRef = useRef(null);
  const [scrapingStatus, setScrapingStatus] = useState({
    status: null,
    message: "",
  });
  const [recruiterId, setRecruiterId] = useState(null);

  // Toggle state for the enhanced layout
  const [isLayoutExpanded, setIsLayoutExpanded] = useState(false);
  const {startTourForRoute, completedTours } = useTour();
  const { enableFocusMode, disableFocusMode } = useFocusMode(); // Add this line
  const [runTour, setRunTour] = useState(false);
  const userInfo = useFetchUser();
  const first_time = userInfo.formData?.first_time; // Add optional chaining
  
  // Quick fix: Ensure focus mode is disabled when component mounts
  useEffect(() => {
    console.log('DataImport component mounted - disabling focus mode as a precaution');
    disableFocusMode();
    
    // Also add a delayed disable as a failsafe
    const timer = setTimeout(() => {
      console.log('Delayed focus mode disable');
      disableFocusMode();
    }, 1000);
    
    return () => {
      clearTimeout(timer);
    };
  }, [disableFocusMode]);
  
  // Enable focus mode when component mounts for first-time users
  useEffect(() => {
    let focusModeTimer;
    
    // NEW: Only enable focus mode if we're actually showing a tour
    const hasSeenDataImportTour = localStorage.getItem('tour_/talent/dataimport');
    
    if (first_time && !hasSeenDataImportTour && startTourForRoute) {
      console.log('Enabling focus mode for first-time user on dataimport page');
      enableFocusMode({
        overlayColor: 'rgba(0, 0, 0, 0.6)',
        highlightBeacon: true
      });
      
      // Failsafe: disable focus mode after 30 seconds in case tour doesn't complete
      focusModeTimer = setTimeout(() => {
        console.log('Failsafe: Disabling focus mode after 30 seconds');
        disableFocusMode();
      }, 30000);
    } else {
      // NEW: Ensure focus mode is disabled for returning users
      console.log('Disabling focus mode for returning user or completed tour');
      disableFocusMode();
    }
    
    // Disable focus mode when component unmounts
    return () => {
      if (focusModeTimer) {
        clearTimeout(focusModeTimer);
      }
      disableFocusMode();
      
      // Additional cleanup
      document.body.classList.remove('focus-mode');
      const style = document.getElementById('tour-focus-mode-styles');
      if (style) {
        style.remove();
      }
    };
  }, [first_time, enableFocusMode, disableFocusMode, startTourForRoute]);
  
  // NEW: Additional cleanup effect to ensure focus mode is disabled
  useEffect(() => {
    // Disable focus mode immediately when component mounts
    disableFocusMode();
    
    // Add another failsafe timeout
    const cleanupTimer = setTimeout(() => {
      console.log('Additional failsafe: Disabling focus mode');
      disableFocusMode();
      
      // Try to remove any existing focus mode styles
      const focusStyles = document.getElementById('tour-focus-mode-styles');
      if (focusStyles) {
        focusStyles.remove();
      }
      
      // Remove focus-mode class from body
      document.body.classList.remove('focus-mode');
    }, 500);
    
    return () => {
      clearTimeout(cleanupTimer);
      disableFocusMode();
    };
  }, [disableFocusMode]);
  
  // NEW: Additional cleanup effect to ensure focus mode is disabled when navigating
  useEffect(() => {
    // Disable focus mode immediately when component mounts
    disableFocusMode();
    
    // Add another failsafe timeout
    const cleanupTimer = setTimeout(() => {
      console.log('Additional failsafe: Disabling focus mode');
      disableFocusMode();
      
      // Try to remove any existing focus mode styles
      const focusStyles = document.getElementById('tour-focus-mode-styles');
      if (focusStyles) {
        focusStyles.remove();
      }
      
      // Remove focus-mode class from body
      document.body.classList.remove('focus-mode');
    }, 500);
    
    // NEW: Disable focus mode when component unmounts
    return () => {
      clearTimeout(cleanupTimer);
      disableFocusMode();
      
      // Additional cleanup
      document.body.classList.remove('focus-mode');
      const style = document.getElementById('tour-focus-mode-styles');
      if (style) {
        style.remove();
      }
    };
  }, [disableFocusMode]);
  
  // NEW: Listen for location changes to ensure focus mode is disabled
  useEffect(() => {
    const handleLocationChange = () => {
      if (window.location.pathname.startsWith('/talent/dataimport')) {
        console.log('Location changed to dataimport page - disabling focus mode');
        disableFocusMode();
        document.body.classList.remove('focus-mode');
        const style = document.getElementById('tour-focus-mode-styles');
        if (style) {
          style.remove();
        }
      }
    };
    
    // Check on initial load
    handleLocationChange();
    
    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleLocationChange);
    
    // Cleanup
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, [disableFocusMode]);

  // Log when component is unmounted
  React.useEffect(() => {
    return () => {
      console.log(`DataImportSection unmounted - Component ID: ${componentId}`);
      // Ensure focus mode is disabled when component unmounts
      disableFocusMode();
      document.body.classList.remove('focus-mode');
      const style = document.getElementById('tour-focus-mode-styles');
      if (style) {
        style.remove();
      }
    };
  }, [componentId, disableFocusMode]);
  
  // NEW: Expose manual reset function for debugging
  useEffect(() => {
    // Expose function globally for manual testing
    window.resetDataImportFocusMode = () => {
      console.log('Manually resetting DataImport focus mode');
      disableFocusMode();
      document.body.classList.remove('focus-mode');
      const style = document.getElementById('tour-focus-mode-styles');
      if (style) {
        style.remove();
      }
      
      // Also try to remove any overlay elements
      const overlays = document.querySelectorAll('.focus-overlay');
      overlays.forEach(overlay => {
        overlay.style.display = 'none';
        overlay.remove();
      });
      
      // Reset pointer events on all elements
      const allElements = document.querySelectorAll('*');
      allElements.forEach(element => {
        element.style.pointerEvents = '';
      });
    };
    
    console.log('DataImport focus mode reset function available. Call window.resetDataImportFocusMode() to manually reset.');
    
    return () => {
      if (window.resetDataImportFocusMode) {
        delete window.resetDataImportFocusMode;
      }
    };
  }, [disableFocusMode, componentId]);
  
  // NEW: Additional cleanup effect to ensure focus mode is disabled when navigating
  useEffect(() => {
    // Disable focus mode immediately when component mounts
    disableFocusMode();
    
    // Add another failsafe timeout
    const cleanupTimer = setTimeout(() => {
      console.log('Additional failsafe: Disabling focus mode');
      disableFocusMode();
      
      // Try to remove any existing focus mode styles
      const focusStyles = document.getElementById('tour-focus-mode-styles');
      if (focusStyles) {
        focusStyles.remove();
      }
      
      // Remove focus-mode class from body
      document.body.classList.remove('focus-mode');
    }, 500);
    
    // NEW: Disable focus mode when component unmounts
    return () => {
      clearTimeout(cleanupTimer);
      disableFocusMode();
      
      // Additional cleanup
      document.body.classList.remove('focus-mode');
      const style = document.getElementById('tour-focus-mode-styles');
      if (style) {
        style.remove();
      }
    // };
  // }, [disableFocusMode]);
      if (window.resetDataImportFocusMode) {
        delete window.resetDataImportFocusMode;
      }
    };
  }, [disableFocusMode, componentId]);
  
  // Define tour steps for the data import page
  const dataImportTourSteps = [
    {
      target: '.data-import-container',
      content: t('tour.dataImportContainer') || 'Welcome to the data import interface. This section allows you to import files and extract data.',
      title: t('tour.Data Import Overview') || 'Data Import Overview',
      placement: 'bottom',
      disableBeacon: true,
      showSkipButton: false,
    },
    {
      target: '.openPaneau',
      content: t('tour.togglePanel') || 'Use this button to expand/collapse the work panel.',
      title: t('tour.Toggle Panel') || 'Work Panel',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '#category-select',
      content: t('tour.categorySelect') || 'Select a main category to classify your imported files.',
      title: t('tour.Category Selection') || 'Category Selection',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '#subcategory-select',
      content: t('tour.subcategorySelect') || 'Select a subcategory for more precise classification.',
      title: t('tour.Subcategory Selection') || 'Subcategory Selection',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '#file-upload',
      content: t('tour.fileUpload') || 'Click here or drag and drop your PDF files to import them.',
      title: t('tour.File Upload') || 'File Upload',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '.start-extraction-button',
      content: t('tour.startExtraction') || 'Start email extraction to automatically retrieve applications.',
      title: t('tour.Email Extraction') || 'Email Extraction',
      placement: 'bottom',
      disableBeacon: true,
    },
  ];

   useEffect(() => {
    // Only show tour for first-time users who haven't completed it yet
    const hasSeenDataImportTour = localStorage.getItem('tour_/talent/dataimport');
    
    // Add a counter to track how many times this effect runs
    const initialEffectRunCount = parseInt(localStorage.getItem('initial_tour_effect_run_count') || '0') + 1;
    localStorage.setItem('initial_tour_effect_run_count', initialEffectRunCount.toString());
    console.log(`Initial tour effect run #${initialEffectRunCount} - Component ID: ${componentId}`);
    console.log('First time user:', first_time);
    console.log('Has seen tour:', hasSeenDataImportTour);
    console.log('Start tour function available:', !!startTourForRoute);
    
    if (first_time && !hasSeenDataImportTour && startTourForRoute) {
      console.log('Showing data import tour for first-time user');
      // Small delay to ensure all elements are rendered
      const timer = setTimeout(() => {
        startTourForRoute('/talent/dataimport');
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [first_time, startTourForRoute, componentId]);
  
  // Function to update first_time status for the user
  const updateFirstTimeStatus = useCallback(async () => {
    // Check if we've already updated first_time to prevent infinite loops
    const firstTimeUpdated = localStorage.getItem('first_time_updated');
    if (firstTimeUpdated === 'true') {
      console.log('First time status already updated, skipping');
      return;
    }
    
    // Check if we're already updating to prevent multiple simultaneous requests
    const isUpdating = localStorage.getItem('first_time_updating');
    if (isUpdating === 'true') {
      console.log('First time status update already in progress, skipping');
      return;
    }
    
    // Set a timestamp to track when the last update was attempted
    const lastUpdateAttempt = localStorage.getItem('first_time_last_update');
    if (lastUpdateAttempt) {
      const timeSinceLastAttempt = Date.now() - parseInt(lastUpdateAttempt);
      // If less than 5 seconds have passed, skip this update
      if (timeSinceLastAttempt < 5000) {
        console.log('First time status update attempted too recently, skipping');
        return;
      }
    }
    
    try {
      // Mark that we're updating
      localStorage.setItem('first_time_updating', 'true');
      // Set the timestamp of this update attempt
      localStorage.setItem('first_time_last_update', Date.now().toString());
      
      // Get user ID from access token
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        console.error("No access token found");
        return;
      }
      
      // Parse JWT to get user ID
      const base64Url = accessToken.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      const decodedToken = JSON.parse(jsonPayload);
      const userId = decodedToken.user_id;
      
      console.log("Updating first_time status for user:", userId);
      
      // Make API call to update first_time status with userId as URL parameter
      const response = await fetch(`${apiUrl}authentication/update-first-time/${userId}/`, {
        method: 'PUT',
        headers: {
          "Content-type": "application/json",
          Authorization: "Bearer " + accessToken,
        }
      });
      
      if (response.ok) {
        console.log("Successfully updated first_time status");
        // Also update localStorage to prevent future calls
        localStorage.setItem('first_time_updated', 'true');
        // Clear the updating flag
        localStorage.removeItem('first_time_updating');
      } else {
        console.error("Failed to update first_time status:", response.status);
        // Clear the updating flag even on failure
        localStorage.removeItem('first_time_updating');
      }
    } catch (error) {
      console.error("Error updating first_time status:", error);
      // Clear the updating flag on error
      localStorage.removeItem('first_time_updating');
    }
  }, [apiUrl]);
  
  useEffect(() => {
    const effectRunCount = parseInt(localStorage.getItem('tour_effect_run_count') || '0') + 1;
    localStorage.setItem('tour_effect_run_count', effectRunCount.toString());
    console.log(`DataImportSection tour effect run #${effectRunCount} - Component ID: ${componentId}`);
    
    const firstTimeUpdated = localStorage.getItem('first_time_updated');
    if (firstTimeUpdated === 'true') {
      console.log('First time status already updated, skipping effect');
      return;
    }
    
    let tourHandled = false;
    
    const handleTourCompleted = (e) => {
      if (tourHandled) {
        console.log('Tour completion already handled in this effect, skipping');
        return;
      }
      
      if (e.detail.route === '/talent/dataimport') {
        console.log('Data import tour completed, updating first_time status');
        tourHandled = true;
        // Disable focus mode when tour is completed
        disableFocusMode();
        updateFirstTimeStatus();
      }
    };
    
    const handleStorageChange = (e) => {
      if (tourHandled) {
        console.log('Tour completion already handled in this effect, skipping');
        return;
      }
      
      if (e.key === 'tour_/talent/dataimport') {
        console.log('Data import tour completed (localStorage change), updating first_time status');
        tourHandled = true;
        // Disable focus mode when tour is completed
        disableFocusMode();
        updateFirstTimeStatus();
      }
    };
    
    // Listen for tour completion events
    window.addEventListener('tourCompleted', handleTourCompleted);
    window.addEventListener('storage', handleStorageChange);
    
    // Cleanup
    return () => {
      window.removeEventListener('tourCompleted', handleTourCompleted);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [componentId, disableFocusMode, updateFirstTimeStatus]);
  
  // NEW: Additional useEffect to handle tour completion
  useEffect(() => {
    const handleTourCompleted = (e) => {
      if (e.detail.route === '/talent/dataimport') {
        console.log('Data import tour completed, disabling focus mode');
        // Disable focus mode when tour is completed
        disableFocusMode();
        
        // Additional cleanup
        document.body.classList.remove('focus-mode');
        const style = document.getElementById('tour-focus-mode-styles');
        if (style) {
          style.remove();
        }
      }
    };
    
    // Listen for tour completion events
    window.addEventListener('tourCompleted', handleTourCompleted);
    
    // Cleanup
    return () => {
      window.removeEventListener('tourCompleted', handleTourCompleted);
    };
  }, [disableFocusMode]);
  
  const toggleLayoutExpanded = () => {
    setIsLayoutExpanded(prev => !prev);
  };
  useEffect(() => {
    if (isUploading) {
      setSelectedCategoryNew(null);
      setSelectedSubCategory(null);
      setSubCategories([]);

      // Also notify parent component about the reset
      onCategorySelect(null);
      onSubCategorySelect(null);
    }
  }, [isUploading, onCategorySelect, onSubCategorySelect]);

  const fetchCategoriesWithSubcategories = async () => {
    try {
      const response = await fetch(`${apiUrl}authentication/categories-with-subcategories/`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Categories with subcategories:', data);

      // If using the version with metadata
      if (data.categories) {
        setCategoriesNew(data.categories);
        console.log(`Found ${data.count} categories`);
      } else {
        // If using the simple version
        setCategoriesNew(data);
      }

    } catch (err) {
      console.error('Error fetching categories:', err);
      // Handle error appropriately
    }
  };

  useEffect(() => {
    const loadCategories = async () => {
      try {
        await fetchCategoriesWithSubcategories();
      } catch (err) {
        console.log(err.message);
      } finally {
        console.log(false);
      }
    };

    loadCategories();

    // Initialize recruiter ID for email scraping
    const id = getUserIdFromAccessToken();
    setRecruiterId(id);

    // Cleanup interval on unmount
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, []);

  // Email scraping utility functions
  const getUserIdFromAccessToken = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      console.log("Access token:", decodedToken);
      return decodedToken.user_id;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  };

  const parseJwt = (token) => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    console.log("payload:", jsonPayload);

    return JSON.parse(jsonPayload);
  };

  const Scrapping = async () => {
    try {
      setIsProcessingOutlook(true);
      console.log("here is the recruiter id: ", recruiterId);
      const response = await fetch(
        `${apiUrl}ai/start_scraping_post/${recruiterId}/?source_call=importData&sujet=${encodeURIComponent(subject)}`,
        {
          headers: {
            "Content-type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
        }
      );

      if (response.status === 401) {
        console.log("Unauthorized. Redirecting to login page...");
        navigate("/login");
        return;
      }

      const result = await response.json();
      console.log("Scraping task initiated: ", result);

      if (result.state === "in_progress" || result.state === true) {
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        pollScrapingStatus(recruiterId);
      } else {
        console.error("Failed to start scraping: ", result.message);
      }
    } catch (error) {
      console.error("Error initiating scraping:", error);
    }
  };

  const pollScrapingStatus = async (id) => {
    try {
      console.log("before the 30s");
      await new Promise((resolve) => setTimeout(resolve, 30000));
      console.log("after the 30s");

      const startTime = Date.now();
      const timeoutDuration = 3 * 60 * 1000;

      intervalIdRef.current = setInterval(async () => {
        try {
          if (Date.now() - startTime > timeoutDuration) {
            console.log("Polling timeout reached (3 minutes). Stopping API calls.");
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
            setIsScrappingLaunched(false);
            statechange(false);
            setfinishing(true);
            setIsProcessingOutlook(false);
            setIsModalOpen(false);
            setScrapingStatus({
              status: "timeout",
              message: "Operation timed out after 3 minutes",
            });
            return;
          }

          const response = await fetch(
            `${apiUrl}ai/get_scraping_status/${recruiterId}/`,
            {
              headers: {
                "Content-type": "application/json",
                Authorization: "Bearer " + localStorage.getItem("accessToken"),
              },
            }
          );

          const result = await response.json();
          console.log("Polling result: ", result);

          setScrapingStatus({
            status: result.state,
            message: result.message,
          });

          if (result.state !== "in_progress" && result.state !== "pending") {
            if (intervalIdRef.current) {
              clearInterval(intervalIdRef.current);
              intervalIdRef.current = null;
            }

            setIsScrappingLaunched(false);
            statechange(false);
            setfinishing(true);

            if (result.state === "success" || result.state === true) {
              setOpenSnackbar(true);
              setIsProcessingOutlook(false);
              setIsModalOpen(false);
            }
          }
        } catch (error) {
          console.error("Error in polling interval:", error);
          setScrapingStatus({
            status: "failed",
            message: "Error occurred while scraping",
          });
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
        }
      }, 5000);
    } catch (error) {
      console.error("Error polling scraping status:", error);
    }
  };

  const code_outlook = async () => {
    try {
      const response = await fetch(`${apiUrl}ai/code_outlook/${recruiterId}/`);
      const result = await response.json();
      console.log("code : ", result.user_code);
      setCode(result.user_code);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  function openAuthPopup() {
    const authUrl = "https://login.microsoftonline.com/common/oauth2/deviceauth";
    const width = window.screen.width / 3;
    const height = window.screen.height;
    const left = window.screen.width - width;
    const top = 0;

    window.open(
      authUrl,
      "authPopup",
      `width=${width},height=${height},top=${top},left=${left}`
    );
  }

  const handleStartGmailScraping = () => {
    if (!subject.trim()) {
      showSnackbar(
        `Submission error: ${JSON.stringify('Veuillez saisir un sujet ou activer la recherche aveugle')}`,
        {
          severity: "warning",
          position: "top-right",
          duration: 3000,
        }
      );
      return;
    }
    setIsProcessing(true);
    setShowGmailAuth(true);
  };

  const handleAuthComplete = (sessionId, result) => {
    console.log("Gmail processing completed:", result);
    setIsProcessing(false);
    setShowGmailAuth(false);
    setIsModalOpen(false);
  };

  const startExtraction = async () => {
    console.log('clicked');
    setIsModalOpen(true);
    resetModal();
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetModal();
  };

  const resetModal = () => {
    setCurrentStep('account');
    setSelectedAccount('');
    setSubject('');
    setIsBlindSearch(false);
  };

  const selectAccount = (accountType) => {
    setSelectedAccount(accountType);
    setCurrentStep('subject');
  };

  const goBackToAccountSelection = () => {
    setCurrentStep('account');
  };

  const toggleBlindSearch = (event) => {
    const newBlindSearch = event.target.checked;
    setIsBlindSearch(newBlindSearch);

    if (newBlindSearch) {
      setSubject('blind');
    } else if (subject === 'blind') {
      setSubject('');
    }
  };

  const handleSubjectChange = (event) => {
    if (!isBlindSearch) {
      setSubject(event.target.value);
    }
  };

  const handleCopy = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(code)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          setSnackbarOpen(true);
        });
    } else {
      setSnackbarOpen(true);
    }
  };

  const hidde_finishing = () => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    setfinishing(false);
    statechange(false);
    setIsScrappingLaunched(false);
    setShowStopMessage(true);
  };

  const handleCloseStopMessage = () => {
    setShowStopMessage(false);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  // Move toggle button to parent container using React Portal
  useEffect(() => {
    const toggleContainer = document.getElementById('toggle-button-container');
    return () => {
      // Cleanup if needed
    };
  }, [isLayoutExpanded]);

  // const toggleLayoutExpanded = () => {
  //   setIsLayoutExpanded(prev => !prev);
  // };

  // Create the toggle button component
  const ToggleButtonPortal = () => {
    const toggleContainer = document.getElementById('toggle-button-container');

    if (!toggleContainer) return null;

    return createPortal(
      <ToggleButton
      className="openPaneau"
        onClick={toggleLayoutExpanded}
        startIcon={
          <Box
            sx={{
              transform: isLayoutExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s ease",
              fontSize: "18px",
            }}
          >
            ▼
          </Box>
        }
      >
        {isLayoutExpanded ? "Réduire le panneau" : "Ouvrir le panneau de travail"}
      </ToggleButton>,
      toggleContainer
    );
  };

  const handleCategoryChangeNew = (e) => {
    const categoryId = parseInt(e.target.value);

    if (categoryId) {
      const selectedCategory = categoriesNew.find(cat => cat.id === categoryId);
      setSelectedCategoryNew(selectedCategory);
      setSubCategories(selectedCategory ? selectedCategory.subcategories : []);

      // Send selected category to parent
      onCategorySelect(selectedCategory);
    } else {
      setSelectedCategoryNew(null);
      setSubCategories([]);

      // Send null to parent when no category is selected
      onCategorySelect(null);
    }

    // Reset subcategory selection when category changes
    setSelectedSubCategory(null);
    onSubCategorySelect(null);
  };

  const handleSubCategoryChange = (e) => {
    const subCategoryId = parseInt(e.target.value);

    if (subCategoryId) {
      const selectedSub = subCategories.find(sub => sub.id === subCategoryId);
      setSelectedSubCategory(selectedSub);

      // Send selected subcategory to parent
      onSubCategorySelect(selectedSub);
    } else {
      setSelectedSubCategory(null);

      // Send null to parent when no subcategory is selected
      onSubCategorySelect(null);
    }
  };

  return (
    <Box className="openPaneau" sx={{ width: "100%", mx: "auto" }}>
      {/* Toggle Button at the top */}
      <Box sx={{ 
        display: "flex", 
        justifyContent: "flex-end", 
        alignItems: "center", 
        mb: 2,
        mt:-2,
        px: 0
      }}>
        <ToggleButton
          onClick={toggleLayoutExpanded}
          startIcon={
            <Box
              sx={{
                transform: isLayoutExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
                fontSize: "18px",
              }}
            >
              ▼
            </Box>
          }
        >
          {isLayoutExpanded ? "Réduire le panneau" : "Importer Les Données"}
        </ToggleButton>
      </Box>

      {/* Enhanced Collapsible Layout */}
      <Collapse in={isLayoutExpanded} timeout={600}>
        <Box sx={{ mt: 3 }}> {/* Increased margin-top from 2 to 3 for more spacing */}
          <EnhancedContainer isExpanded={isLayoutExpanded}>
            <Grid
              container
              spacing={2} // Reduced from 3 to 2
              sx={{
                p: { xs: 1.5, sm: 2, md: 2.5 }, // Reduced padding from { xs: 2, sm: 3, md: 4 }
                minHeight: "auto",
              }}
            >
              {/* Section 1: Categories */}
              <Grid item xs={12} md={4}>
                <SectionCard>
                  <SectionTitle>
                    📁 Catégories
                  </SectionTitle>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{
                        mb: 1.5,
                        mt: 1.5,
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#4a5568",
                        fontFamily: "Poppins"
                      }}>
                        Catégorie principale
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        id="category-select"
                        value={selectedCategoryNew?.id || ""}
                        onChange={handleCategoryChangeNew}
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: "#f8fafc",
                            border: "2px solid #e2e8f0",
                            transition: "all 0.3s ease",
                            "& fieldset": { border: "none" },
                            "&:hover": {
                              borderColor: "#cbd5e0",
                              backgroundColor: "#f1f5f9",
                            },
                            "&.Mui-focused": {
                              borderColor: "#667eea",
                              backgroundColor: "#ffffff",
                              boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.1)",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontFamily: "Poppins",
                            fontSize: "14px",
                          },
                        }}
                      >
                        <MenuItem value="">Sélectionnez une catégorie</MenuItem>
                        {categoriesNew.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name_fr}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{
                        mb: 1.5,
                        mt: 1.5,
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#4a5568",
                        fontFamily: "Poppins"
                      }}>
                        Sous-catégorie
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        id="subcategory-select"
                        value={selectedSubCategory?.id || ""}
                        onChange={handleSubCategoryChange}
                        disabled={!selectedCategoryNew || subCategories.length === 0}
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: !selectedCategoryNew || subCategories.length === 0 ? "#f7fafc" : "#f8fafc",
                            border: "2px solid #e2e8f0",
                            transition: "all 0.3s ease",
                            "& fieldset": { border: "none" },
                            "&:hover": {
                              borderColor: !selectedCategoryNew || subCategories.length === 0 ? "#e2e8f0" : "#cbd5e0",
                              backgroundColor: !selectedCategoryNew || subCategories.length === 0 ? "#f7fafc" : "#f1f5f9",
                            },
                            "&.Mui-focused": {
                              borderColor: "#667eea",
                              backgroundColor: "#ffffff",
                              boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.1)",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontFamily: "Poppins",
                            fontSize: "14px",
                          },
                        }}
                      >
                        <MenuItem value="">
                          {selectedCategoryNew
                            ? "Aucune sous-catégorie disponible"
                            : "Veuillez d'abord sélectionner une catégorie"}
                        </MenuItem>
                        {subCategories.map((subcategory) => (
                          <MenuItem key={subcategory.id} value={subcategory.id}>
                            {subcategory.name_fr}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </Box>
                </SectionCard>
              </Grid>

              {/* Section 2: Import */}
              <Grid item xs={12} md={4}>
                <SectionCard>
                  <SectionTitle>
                    📤 Import de fichiers
                  </SectionTitle>

                  <Grow in={true}>
                    <UploadBox
                      elevation={0}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleFileDrop}
                      sx={{
                        borderColor: dragging ? "#667eea" : "#e2e8f0",
                        borderWidth: "2px",
                        borderStyle: "dashed",
                        borderRadius: "16px",
                        minHeight: "80px", // Further reduced from 120px since icon is removed
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        background: dragging
                          ? "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)"
                          : "#f8fafc",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          borderColor: "#667eea",
                          background: "linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <label htmlFor="file-upload" style={{ cursor: "pointer", textAlign: "center", width: "100%" }}>
                        {/* Removed the icon box to reduce height */}
                        <Typography
                          sx={{
                            color: "#4a5568",
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            fontSize: "14px",
                            mb: 0.5,
                            mt: 1, // Added small top margin to replace the removed icon space
                          }}
                        >
                          {" Cliquez ou Glissez vos fichiers ici"}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#718096",
                            fontFamily: "Poppins",
                            fontWeight: 400,
                            fontSize: "12px",
                          }}
                        >
                          {t("pdfFilesOnly") || "Fichiers PDF uniquement"}
                        </Typography>
                        <VisuallyHiddenInput
                          id="file-upload"
                          type="file"
                          accept=".pdf"
                          multiple
                          onChange={handleFileUpload}
                        />
                      </label>
                    </UploadBox>
                  </Grow>
                </SectionCard>
              </Grid>

              {/* Section 3: Email Extraction */}
              <Grid item xs={12} md={4}>
                <SectionCard>
                  <SectionTitle>
                    🚀 Extraction d'e-mails
                  </SectionTitle>

                  <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "80px", // Further reduced from 120px
                    gap: 2
                  }}>
                    <ActionButton
                      variant="contained"
                      onClick={startExtraction}
                      className="start-extraction-button"
                      sx={{
                        width: "100%",
                        maxWidth: "240px", // Reduced from 280px
                        minHeight: "48px", // Reduced from 60px
                        fontSize: "14px", // Reduced from 16px
                        padding: "16px 24px", // Reduced from "20px 32px"
                      }}
                    >
                      <Box
                        sx={{
                          width: "14", // Reduced from 16
                          height: "14",
                          borderRadius: "50%",
                          // background: "rgba(255, 255, 255, 0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: "8px", // Reduced from 12px
                        }}
                      >
                        <img
                          src={ai_icon}
                          style={{
                            filter: "invert(1) brightness(2)",
                            width: "14px", // Reduced from 16px
                            height: "14px"
                          }}
                        />
                      </Box>
                      {t("startScraping") || "Démarrer l'extraction"}
                    </ActionButton>

                    {/* Enhanced Result Display */}
                    {result && (
                      <Fade in={true} timeout={600}>
                        <Box sx={{
                          p: 2,
                          background: "linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)",
                          border: "2px solid #b0d4c1",
                          borderRadius: 12,
                          textAlign: 'center',
                          width: '100%',
                          maxWidth: "220px",
                          position: "relative",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "3px",
                            background: "linear-gradient(90deg, #28a745, #20c997)",
                          }
                        }}>
                          <Typography sx={{
                            fontFamily: 'Poppins',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#155724',
                            mb: 0.5
                          }}>
                            ✅ Configuration active
                          </Typography>
                          <Typography sx={{
                            fontFamily: 'Poppins',
                            fontSize: 12,
                            color: '#155724',
                            opacity: 0.8
                          }}>
                            <strong>{result.accountType}</strong><br />
                            {result.subject}
                          </Typography>
                        </Box>
                      </Fade>
                    )}
                  </Box>
                </SectionCard>
              </Grid>
            </Grid>
          </EnhancedContainer>
        </Box> {/* Close the margin Box wrapper */}
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {showSuccessAnimation && (
        <Fade in={showSuccessAnimation} timeout={600}>
          <Alert severity="success" sx={{ mt: 2 }}>
            {t("filesUploadedSuccessfully")}
          </Alert>
        </Fade>
      )}
      {showDataScrapingAnimation && (
        <Fade in={showDataScrapingAnimation} timeout={1000}>
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <AnimatedDataIcon />
            <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
              {t("dataScrapingInProgress")}
            </Typography>
          </Box>
        </Fade>
      )}

      {/* Dynamic Import Progress Bar */}
      {showImportProgress && (
        <Box sx={{ width: '100%', mb: 3, maxWidth: '600px', margin: '0 auto', mt: 2 }}>
          <Typography sx={{ mb: 1, fontFamily: 'Poppins', textTransform: 'none' }}>
            {t('dataScrapingInProgress') || 'Processing imported data...'} ({importFileCount} {importFileCount === 1 ? 'file' : 'files'})
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={uploadProgress} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#0017E7'
              }
            }} 
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: 'Poppins' }}>
              {Math.round(uploadProgress)}% Complete
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'Poppins', color: 'text.secondary' }}>
              {estimatedTimeRemaining > 0 ? `~${Math.ceil(estimatedTimeRemaining)}s remaining` : 'Finalizing...'}
            </Typography>
          </Box>
        </Box>
      )}

      {files.length > 0 && (
        <Paper sx={{
          mt: 0,
          borderRadius: "20px",
          boxShadow: 'none',
        }} elevation={3}>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
            }}
          >
            <Typography
              sx={{
                color: "#000",
                fontFamily: "Poppins",
                fontWeight: 400,
                fontSize: "18px",
                lineHeight: "100%",
                letterSpacing: "0%",
              }}
            >
              {t("uploadedFiles")} ({files.length})
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                alignItems: "center",
              }}
            >
              <UpdateButton
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontFamily: "Poppins",
                  px: 4,
                  py: 1.5,
                  fontWeight: "500",
                  boxShadow: "none",
                  minWidth: "120px",
                  height: "40px",
                }}
                startIcon={<CloudUploadIcon />}
                onClick={uploadFiles}
                disabled={isUploading || files.length === 0}
              >
                {isUploading ? t("uploading") : t("uploadFilesButton")}
              </UpdateButton>

              <CancelButton
                sx={{
                  bgcolor: "#fff",
                  color: "#FF0000",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontFamily: "Poppins",
                  px: 4,
                  py: 1.5,
                  fontWeight: 400,
                  fontSize: "12px",
                  minWidth: "120px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",

                  "&:hover": {
                    bgcolor: "#FF0000",
                    borderColor: "#FF0000",
                    "& .MuiTypography-root": {
                      color: "#FFFFFF",
                    },
                    "& img": {
                      filter: "brightness(0) invert(1)",
                    },
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#e0e0e0",
                    borderColor: "#e0e0e0",
                    "& .MuiTypography-root": {
                      color: "#9e9e9e",
                    },
                    "& img": {
                      opacity: 0.5,
                    },
                  },
                  "& .MuiTypography-root": {
                    color: "#FF0000",
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    fontSize: "12px",
                  },
                }}
                onClick={removeAllFiles}
                disabled={isUploading || showImportProgress}
                variant="outlined"
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <img src={cancel_icon} alt="Cancel" />
                  <Typography>{t("removeAll")}</Typography>
                </Box>
              </CancelButton>
            </Box>
          </Box>

          {/* Use the ModernFileTable component */}
          <ModernFileTable
            files={getPaginatedFiles()}
            onDelete={removeFile}
            t={t}
          />

          {/* Use the PaginationControls component passed as prop */}
          {PaginationControls && (
            <PaginationControls
              files={files}
              itemsPerPage={itemsPerPage}
              page={page}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          )}
        </Paper>
      )}

      {/* Email Scraping Modal */}
      <Modal
        open={isModalOpen}
        onClose={closeModal}
        sx={{
          '& .MuiBackdrop-root': {
            backdropFilter: 'blur(5px)',
          }
        }}
      >
        <ModalBox>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            borderBottom: '1px solid #e0e0e0'
          }}>
            <Typography variant="h6" sx={{ fontFamily: 'Poppins', fontWeight: 600 }}>
              {currentStep === 'account' ? 'Choisissez le type de compte' : "Configuration de la recherche d'e-mails"}
            </Typography>
            <IconButton onClick={closeModal} sx={{ color: '#666' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3 }}>
            {currentStep === 'account' && (
              <Box>
                <Typography sx={{
                  textAlign: 'center',
                  color: '#666',
                  mb: 3,
                  fontFamily: 'Poppins',
                  fontSize: '15px'
                }}>
                  Sélectionnez votre fournisseur de messagerie pour continuer
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <AccountButton onClick={() => selectAccount('Google')}>
                    <Box
                      component="img"
                      src={googleticon}
                      alt="Google"
                      sx={{
                        width: 48,
                        height: 48,
                        mb: 1.5,
                        borderRadius: 2,
                        backgroundColor: '#fff',
                        objectFit: 'contain',
                      }}
                    />
                    <Typography sx={{ fontFamily: 'Poppins', fontWeight: 600, color: '#333' }}>
                      Google
                    </Typography>
                  </AccountButton>

                  <AccountButton onClick={() => selectAccount('Microsoft')} >
                    <Box
                      component="img"
                      src={microsofticon}
                      alt="Microsoft"
                      sx={{
                        width: 48,
                        height: 48,
                        mb: 1.5,
                        borderRadius: 2,
                        backgroundColor: '#fff',
                        objectFit: 'contain',
                      }}
                    />
                    <Typography sx={{ fontFamily: 'Poppins', fontWeight: 600, color: '#333' }}>
                      Microsoft
                    </Typography>
                  </AccountButton>
                </Box>
              </Box>
            )}

            {/* Step 2: Subject Form */}
            {currentStep === 'subject' && (
              <Box>
                {/* Selected Account Info */}
                <Box sx={{
                  backgroundColor: '#f5f5f5',
                  borderLeft: '4px solid #0017E7',
                  p: 2,
                  borderRadius: 1,
                  mb: 3
                }}>
                  <Typography sx={{ fontFamily: 'Poppins', color: '#333' }}>
                    <strong>Type de compte sélectionné:</strong> {selectedAccount}
                  </Typography>
                </Box>

                {/* Subject Input */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography
                      sx={{ fontFamily: "Poppins", fontWeight: 600, color: "#333", mr: 1 }}
                    >
                      Sujet de l'email
                    </Typography>

                    <BigTooltip
                      title="⚠️ L'objet sera utilisé comme mot-clé principal pour rechercher et extraire les e-mails de votre boîte de réception. Assurez-vous qu'il est exact. Si vous souhaitez effectuer une recherche aveugle sur tous vos e-mails non lus, cochez la case « Recherche aveugle »."
                      arrow
                    >
                      <Box
                        sx={{
                          width: 15,
                          height: 15,
                          borderRadius: "50%",
                          bgcolor: "#1976d2",
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        ?
                      </Box>
                    </BigTooltip>
                  </Box>

                  <TextField
                    fullWidth
                    value={subject === 'blind' ? '' : subject}
                    onChange={handleSubjectChange}
                    disabled={isBlindSearch}
                    placeholder="Enter email subject to search for..."
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontFamily: 'Poppins',
                        backgroundColor: isBlindSearch ? '#f5f5f5' : '#fff',
                        '&.Mui-focused fieldset': {
                          borderColor: '#0017E7',
                        }
                      }
                    }}
                  />
                </Box>

                {/* Blind Search Checkbox */}
                {/* Blind Search Checkbox with Danger Warning */}
                <Box sx={{ mb: 3, p: 2, border: '2px solid #ff4444', borderRadius: 2, backgroundColor: '#fff5f5' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isBlindSearch}
                        onChange={toggleBlindSearch}
                        sx={{
                          color: '#ff4444',
                          '&.Mui-checked': {
                            color: '#cc0000',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(255, 68, 68, 0.1)',
                          }
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{
                          fontFamily: 'Poppins',
                          fontSize: 14,
                          color: '#cc0000',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          ⚠️ Recherche globale (DANGER)
                        </Typography>
                        <Typography sx={{
                          fontFamily: 'Poppins',
                          fontSize: 12,
                          color: '#666',
                          mt: 0.5,
                          fontStyle: 'italic'
                        }}>
                          Cette option peut extracter TOUS vos emails. Soyez absolument certain avant de continuer.
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

                {/* Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={goBackToAccountSelection}
                    startIcon={<ArrowBackIcon />}
                    sx={{
                      fontFamily: 'Poppins',
                      textTransform: 'none',
                      borderColor: '#666',
                      color: '#666',
                      '&:hover': {
                        borderColor: '#333',
                        backgroundColor: 'rgba(102, 102, 102, 0.04)'
                      }
                    }}
                  >
                    Retour
                  </Button>
                  {selectedAccount === 'Google' ? (
                    <NewPostButton
                      sx={{
                        gap: 1,
                        width: "45%",
                        justifyContent: { xs: "center", sm: "flex-start" },
                        "&:hover .MuiSvgIcon-root": {
                          color: "white",
                          transition: "color 0.3s ease",
                        },
                      }}
                      startIcon={<AutoModeIcon />}
                      onClick={handleStartGmailScraping}
                      disabled={isProcessing}
                    >
                      {isProcessing ? "Processing Gmail..." : "Gmail Scraping"}
                    </NewPostButton>
                  ) : (
                    <NewPostButton
                      sx={{
                        gap: 1,
                        width: "45%",
                        justifyContent: { xs: "center", sm: "flex-start" },
                        "&:hover .MuiSvgIcon-root": {
                          color: "white",
                          transition: "color 0.3s ease",
                        },
                      }}
                      onClick={() => {
                        if (!subject.trim()) {
                          showSnackbar(
                            `Erreur de recherche : veuillez saisir un sujet ou activer la recherche aveugle.`,
                            {
                              severity: "warning",
                              position: "top-right",
                              duration: 3000,
                            }
                          );
                          return;
                        }
                        statechange(true);
                        Scrapping();
                      }}
                      disabled={isProcessingOutlook}
                      onMouseUp={() => {
                        if (!subject.trim()) {
                          return;
                        }
                        setTimeout(() => {
                          code_outlook();
                          setIsScrappingLaunched(true);
                        }, 2000);
                        setTimeout(() => {
                          openAuthPopup();
                        }, 5000);
                      }}
                      startIcon={<AutoModeIcon />}
                    >
                      {isProcessing ? "Processing Outlook..." : "Outlook Scraping"}
                    </NewPostButton>
                  )}
                </Box>

                {showGmailAuth && (
                  <GmailAuth
                    onAuthComplete={handleAuthComplete}
                    postId={getUserIdFromAccessToken()}
                    sourceCall='importData'
                    sujet={subject}
                  />
                )}

                {isScrappingLaunched && (
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Button variant="outlined" sx={{ mr: 2 }}>
                      {code}
                    </Button>
                    <Tooltip title={copied ? "Copied!" : "Copy code"}>
                      <IconButton onClick={handleCopy} size="small">
                        {copied ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <ContentCopyIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </ModalBox>
      </Modal>

      {/* Loading and Status Components */}
      <Box
        sx={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 9999,
          display: scrapingStatus.status === "in_progress" || showLoading ? "flex" : "none",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          padding: "10px 20px",
          borderRadius: "8px",
          gap: "10px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
        }}
      >
        <CircularProgress size={24} thickness={4} sx={{ color: "#4CAF50" }} />
        <Typography sx={{ fontSize: "1rem", color: "#fff" }}>
          {scrapingStatus.message || "Scraping in progress..."}
        </Typography>
        <IconButton
          size="small"
          onClick={hidde_finishing}
          sx={{ color: "white", marginLeft: "5px" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Snackbars */}
      <Snackbar
        open={showStopMessage}
        onClose={handleCloseStopMessage}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseStopMessage}
          severity="warning"
          sx={{ width: "100%", "& .MuiAlert-message": { fontSize: "1rem" } }}
        >
          <AlertTitle>Scraping Stopped</AlertTitle>
          The scraping process has been stopped.
        </Alert>
      </Snackbar>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%", "& .MuiAlert-message": { fontSize: "1rem" } }}
        >
          <AlertTitle>Success</AlertTitle>
          {scrapingStatus.message || "Scraping completed successfully!"}
        </Alert>
      </Snackbar>

      <Snackbar
        open={scrapingStatus.status === "failed"}
        autoHideDuration={2000}
        onClose={() => setScrapingStatus({ status: null, message: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setScrapingStatus({ status: null, message: "" })}
          severity="error"
          sx={{ width: "100%", "& .MuiAlert-message": { fontSize: "1rem" } }}
        >
          <AlertTitle>Error</AlertTitle>
          {scrapingStatus.message || "Scraping process failed"}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DataImportSection;